import { Link } from "react-router-dom";
import { Scale, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-background py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <Button variant="ghost" asChild className="mb-6">
          <Link to="/">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
        </Button>

        <div className="flex items-center gap-3 mb-8">
          <Scale className="h-8 w-8 text-gold" />
          <h1 className="font-serif text-3xl font-bold">Terms of Service</h1>
        </div>

        <div className="prose prose-invert max-w-none space-y-6 text-foreground">
          <p className="text-muted-foreground">
            Last updated: November 3, 2026
          </p>

          <section className="space-y-4">
            <h2 className="font-serif text-xl font-bold text-gold">1. Acceptance of Terms</h2>
            <p className="text-muted-foreground">
              By accessing or using Bar Songz ("Service"), you agree to be bound by these Terms of 
              Service ("Terms"). If you do not agree to these Terms, you may not access or use the Service.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="font-serif text-xl font-bold text-gold">2. Description of Service</h2>
            <p className="text-muted-foreground">
              Bar Songz provides educational audio content and materials related to law school 
              studies. Our Service is intended for educational purposes only and does not constitute 
              legal advice.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="font-serif text-xl font-bold text-gold">3. User Accounts</h2>
            <p className="text-muted-foreground">
              To access certain features, you must create an account. You agree to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Provide accurate and complete information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized use</li>
              <li>Accept responsibility for all activities under your account</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="font-serif text-xl font-bold text-gold">4. Subscription and Payments</h2>
            <p className="text-muted-foreground">
              Subscription fees are billed in advance on a monthly or annual basis. All payments are 
              non-refundable except as required by law or as expressly stated in these Terms. We reserve 
              the right to change our prices with 30 days' notice.
            </p>
          </section>

          <section className="space-y-4 bg-card/50 p-6 rounded-lg border border-gold/20">
            <h2 className="font-serif text-xl font-bold text-gold">5. DISCLAIMER OF WARRANTIES</h2>
            <p className="text-muted-foreground uppercase text-sm">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
              EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF 
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p className="text-muted-foreground uppercase text-sm">
              WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE. 
              THE CONTENT PROVIDED IS FOR EDUCATIONAL PURPOSES ONLY AND SHOULD NOT BE RELIED UPON 
              AS LEGAL ADVICE. ALWAYS CONSULT A LICENSED ATTORNEY FOR LEGAL MATTERS.
            </p>
          </section>

          <section className="space-y-4 bg-card/50 p-6 rounded-lg border border-gold/20">
            <h2 className="font-serif text-xl font-bold text-gold">6. No Guarantee of Results</h2>
            <p className="text-muted-foreground">
              Bar Songz is a study aid intended to supplement — not replace — traditional legal 
              education materials such as casebooks, outlines, lectures, and licensed bar-preparation 
              courses. Use of the Service does <strong className="text-foreground">not</strong> guarantee any 
              particular outcome, including but not limited to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Admission to, performance in, or graduation from any law school</li>
              <li>Any specific grade, class rank, GPA, or course outcome</li>
              <li>Passing any bar examination, MPRE, MBE, MEE, MPT, UBE, or any state-specific licensing examination</li>
              <li>Admission to the bar of any state, territory, or jurisdiction</li>
              <li>Securing employment, internships, clerkships, or any professional opportunity</li>
            </ul>
            <p className="text-muted-foreground">
              No testimonial, endorsement, or example outcome shown on barsongz.com should be 
              interpreted as a typical or expected result. Bar Songz makes no representations, 
              warranties, or guarantees, express or implied, regarding the educational efficacy 
              of the Service or the outcomes any user may achieve.
            </p>
            <p className="text-muted-foreground">
              You are solely responsible for your own academic and professional preparation. Nothing on 
              the Service constitutes legal advice or the practice of law.
            </p>
          </section>

          <section className="space-y-4 bg-card/50 p-6 rounded-lg border border-gold/20">
            <h2 className="font-serif text-xl font-bold text-gold">7. LIMITATION OF LIABILITY & HOLD HARMLESS</h2>
            <p className="text-muted-foreground uppercase text-sm">
              TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, THE JURIS ARCHIVE, ITS OFFICERS, 
              DIRECTORS, EMPLOYEES, AGENTS, AND AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, 
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO 
              LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground uppercase text-sm">
              <li>YOUR ACCESS TO OR USE OF (OR INABILITY TO ACCESS OR USE) THE SERVICE</li>
              <li>ANY CONDUCT OR CONTENT OF ANY THIRD PARTY ON THE SERVICE</li>
              <li>ANY CONTENT OBTAINED FROM THE SERVICE</li>
              <li>UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR TRANSMISSIONS OR CONTENT</li>
            </ul>
            <p className="text-muted-foreground uppercase text-sm mt-4">
              YOU AGREE TO INDEMNIFY, DEFEND, AND HOLD HARMLESS THE JURIS ARCHIVE AND ITS OFFICERS, 
              DIRECTORS, EMPLOYEES, AGENTS, AND AFFILIATES FROM AND AGAINST ANY AND ALL CLAIMS, 
              DAMAGES, OBLIGATIONS, LOSSES, LIABILITIES, COSTS, AND EXPENSES ARISING FROM YOUR USE 
              OF THE SERVICE OR VIOLATION OF THESE TERMS.
            </p>
            <p className="text-muted-foreground uppercase text-sm mt-4">
              IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU EXCEED THE AMOUNT YOU PAID TO US IN THE 
              TWELVE (12) MONTHS PRECEDING THE CLAIM.
            </p>
          </section>

          <section className="space-y-4 bg-card/50 p-6 rounded-lg border border-red-500/30">
            <h2 className="font-serif text-xl font-bold text-red-400">8. BINDING ARBITRATION & CLASS ACTION WAIVER</h2>
            <p className="text-muted-foreground">
              <strong className="text-foreground">PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS.</strong>
            </p>
            <p className="text-muted-foreground">
              Any dispute, controversy, or claim arising out of or relating to these Terms or the Service, 
              including the determination of the scope or applicability of this agreement to arbitrate, 
              shall be determined by binding arbitration in <strong className="text-foreground">Newark, New Jersey</strong>, 
              before a single arbitrator.
            </p>
            <p className="text-muted-foreground">
              The arbitration shall be administered by JAMS pursuant to its Comprehensive Arbitration Rules 
              and Procedures. Judgment on the award may be entered in any court having jurisdiction.
            </p>
            <p className="text-muted-foreground mt-4">
              <strong className="text-foreground">CLASS ACTION WAIVER:</strong> YOU AND THE JURIS ARCHIVE AGREE THAT 
              EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY AND NOT AS A 
              PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING.
            </p>
            <p className="text-muted-foreground mt-4">
              <strong className="text-foreground">Opt-Out:</strong> You may opt out of this arbitration agreement 
              by sending written notice to legal@barsongs.com within 30 days of first accepting these Terms.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="font-serif text-xl font-bold text-gold">9. Governing Law</h2>
            <p className="text-muted-foreground">
              These Terms shall be governed by and construed in accordance with the laws of the 
              <strong className="text-foreground"> State of New Jersey</strong>, without regard to its conflict 
              of law provisions. Any legal action or proceeding not subject to arbitration shall be brought 
              exclusively in the state or federal courts located in <strong className="text-foreground">Essex County, New Jersey</strong>.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="font-serif text-xl font-bold text-gold">10. Intellectual Property</h2>
            <p className="text-muted-foreground">
              All content on the Service, including but not limited to audio files, text, graphics, logos, 
              and software, is the property of Bar Songz or its licensors and is protected by 
              copyright, trademark, and other intellectual property laws.
            </p>
          </section>

          <section className="space-y-4 bg-card/50 p-6 rounded-lg border border-gold/20">
            <h2 className="font-serif text-xl font-bold text-gold">11. Downloads, "Listen Forever," & License Scope</h2>
            <p className="text-muted-foreground">
              When you purchase a Course Pack, Pick-Any-4 Pack, or any other one-time download product 
              offered on barsongz.com, the following terms apply:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>
                <strong className="text-foreground">Full-length file delivered at purchase.</strong>{" "}
                Each downloaded file is the complete song as offered for sale. Bar Songz has no 
                obligation to re-deliver, re-host, or otherwise re-transmit any file after the initial 
                download has been made available to you.
              </li>
              <li>
                <strong className="text-foreground">"Listen forever" is your responsibility.</strong>{" "}
                The phrase "download now, listen forever" — and any similar marketing language used on 
                barsongz.com — refers solely to the fact that the file is delivered to you to keep on 
                your own device(s). Bar Songz does not guarantee perpetual cloud storage, perpetual 
                re-download links, or perpetual playback compatibility with any device, operating system, 
                application, or file format that may exist now or in the future. Maintaining backups, 
                preserving access to the file, and ensuring continued playback on devices you own are 
                your sole responsibility.
              </li>
              <li>
                <strong className="text-foreground">No transfer of ownership or licensing rights.</strong>{" "}
                Downloading a file from barsongz.com grants you a limited, personal, non-exclusive, 
                non-transferable, non-sublicensable license to listen to that recording for your own 
                personal, non-commercial study use. It does <em>not</em> transfer any ownership of, 
                or licensing rights in, the underlying musical composition, lyrics, sound recording, 
                master recording, performance, arrangement, or any related intellectual property. 
                <strong className="text-foreground"> Bar Songz and barsongz.com retain all such rights</strong>, 
                including (without limitation) all copyrights, mechanical rights, synchronization rights, 
                public-performance rights, distribution rights, derivative-work rights, and any neighboring 
                or related rights, in every jurisdiction.
              </li>
              <li>
                <strong className="text-foreground">Prohibited uses.</strong>{" "}
                You may not redistribute, resell, sublicense, publicly perform, publicly stream, broadcast, 
                synchronize, sample, remix, modify, or create derivative works from any downloaded file, 
                in whole or in part, without express prior written permission from Bar Songz. Removing, 
                altering, or obscuring any embedded metadata, watermark, or ownership notice is also 
                strictly prohibited.
              </li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="font-serif text-xl font-bold text-gold">12. User Conduct</h2>
            <p className="text-muted-foreground">You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Share your account credentials or allow others to access your account</li>
              <li>Download, copy, or distribute content without authorization</li>
              <li>Use the Service for any unlawful purpose</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Attempt to gain unauthorized access to any part of the Service</li>
              <li>Harass, abuse, or harm other users</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="font-serif text-xl font-bold text-gold">13. Termination</h2>
            <p className="text-muted-foreground">
              We may terminate or suspend your account and access to the Service immediately, without 
              prior notice or liability, for any reason, including breach of these Terms. Upon termination, 
              your right to use the Service will immediately cease.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="font-serif text-xl font-bold text-gold">14. Changes to Terms</h2>
            <p className="text-muted-foreground">
              We reserve the right to modify these Terms at any time. We will provide notice of material 
              changes by posting the updated Terms on this page and updating the "Last updated" date. 
              Your continued use of the Service after changes constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="font-serif text-xl font-bold text-gold">15. Severability</h2>
            <p className="text-muted-foreground">
              If any provision of these Terms is held to be invalid or unenforceable, the remaining 
              provisions will continue in full force and effect.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="font-serif text-xl font-bold text-gold">16. Contact Information</h2>
            <p className="text-muted-foreground">
              For questions about these Terms, please contact us at:
            </p>
            <p className="text-muted-foreground">
              Email: legal@barsongs.com<br />
              Bar Songz, Legal Department<br />
              New Jersey, United States
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
